/**
 * BinaryIndicatorBlock.js
 *
 * Chart-style timeline for binary (yes/no) indicators. Progress/status is shown
 * in the same Progress & assessment section as other indicators (card-container,
 * indicator-detail-section--progress). This block only renders the timeline:
 * years on the bottom (x-axis), Yes/No values above each year.
 * Used when indicatorType === 'binary'.
 */

import React from 'react';
import '../utils/sharedStyles.css';

const BINARY_VALUE_YES = 1;
const BINARY_VALUE_NO = 0;
const MIN_YEAR = 2015;

function toBinaryDisplay(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return BINARY_VALUE_NO;
  return Number(value) >= 0.5 ? BINARY_VALUE_YES : BINARY_VALUE_NO;
}

function getBinaryTimeline(chartData) {
  if (!chartData?.labels?.length || !chartData?.datasets?.length) return [];
  const years = chartData.labels;
  const data = chartData.datasets[0]?.data;
  if (!Array.isArray(data) || years.length !== data.length) return [];
  const pairs = years.map((y, i) => {
    const year = typeof y === 'string' ? parseInt(y, 10) : Number(y);
    const val = toBinaryDisplay(data[i]);
    return { year: !Number.isNaN(year) ? year : null, value: val, label: val === BINARY_VALUE_YES ? 'Yes' : 'No' };
  });
  return pairs
    .filter((p) => p.year !== null && p.year >= MIN_YEAR)
    .sort((a, b) => a.year - b.year);
}

const BinaryIndicatorBlock = ({ indicator }) => {
  const timeline = getBinaryTimeline(indicator?.chartData);
  if (timeline.length === 0) return null;

  return (
    <div className="binary-timeline-chart" role="region" aria-label="Status by year">
      <div className="binary-timeline-chart__body">
        <div className="binary-timeline-chart__values-row">
          {timeline.map(({ year, label }) => (
            <div key={year} className="binary-timeline-chart__cell">
              <span className={`binary-timeline-chart__value binary-timeline-chart__value--${label.toLowerCase()}`}>
                {label}
              </span>
            </div>
          ))}
        </div>
        <div className="binary-timeline-chart__axis">
          {timeline.map(({ year }) => (
            <div key={year} className="binary-timeline-chart__tick">
              {year}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BinaryIndicatorBlock;
